import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Popup, Popup2 } from '../../components/Popup';
import AudioButton from '../../components/AudioButton';

import '../../assets/scss/challenge.scss';

const ChallengeView = (props) => {
   const navigate = useNavigate();
   const location = useLocation();
   const [searchParams] = useSearchParams();

   const pathname = location.pathname.substring(1);
   const isStudy = pathname.startsWith('Study');

   const countParam = searchParams.get('count');
   const count = countParam ? parseInt(countParam) : 10;
   const chapter = searchParams.get('chapter');

   const [words, setWords] = useState([]);
   const [currentIndex, setCurrentIndex] = useState(0);
   const [score, setScore] = useState(0);
   const [wrongCount, setWrongCount] = useState(0);
   const [userInput, setUserInput] = useState('');
   const [details, setDetails] = useState([]);

   const [showHint, setShowHint] = useState(false);
   const [isFlipped, setIsFlipped] = useState(false);
   const [feedback, setFeedback] = useState(null); // null | { isCorrect }
   const inputRef = useRef(null);
   const isReview = !!(location.state && location.state.review);

   useEffect(() => {
      if (location.state && location.state.reviewWords) {
         setWords(location.state.reviewWords);
      } else {
         fetchWords();
      }
   }, []);

   useEffect(() => {
      if (inputRef.current) inputRef.current.focus();
   }, [currentIndex, words]);

   // Reset per-question state in the same update as the index change; resetting in an effect left one
   // render with the old `feedback`, so a fast double press skipped the next question ungraded.
   const goTo = (index) => {
      setCurrentIndex(index);
      setUserInput('');
      setShowHint(false);
      setIsFlipped(false);
      setFeedback(null);
   };

   const fetchWords = async () => {
      try {
         let url = `/api/words?count=${count}`;
         if (chapter) url += `&chapter=${chapter}`;
         const response = await axios.get(url);

         // Response is now { words: [], unlockedDay: N, ... }
         if (response.data && response.data.words) {
            setWords(response.data.words);
         } else if (Array.isArray(response.data)) {
            setWords(response.data); // Fallback for old API style
         }
      } catch (error) {
         console.error('Failed to fetch words:', error);
         alert('단어를 불러오는데 실패했습니다. 로그인이 필요하거나 잠긴 단계일 수 있습니다.');
      }
   };

   // Ignore case, extra spaces and parenthesized notes like "mega (unit prefix)"
   const normalize = (s) => s.toLowerCase().replace(/\(.*?\)/g, '').replace(/\s+/g, ' ').trim();

   const handleNext = () => {
      if (words.length === 0) return;
      const currentWord = words[currentIndex];

      // First press grades and reveals the answer; second press moves on.
      // In study mode typing is optional, so only grade when something was typed.
      if (!feedback && (!isStudy || userInput.trim())) {
         const isCorrect = normalize(userInput) !== '' && normalize(userInput) === normalize(currentWord.word);
         setDetails(prev => [...prev, {
            word: currentWord.word,
            meaning: currentWord.meaning,
            user_input: userInput,
            is_correct: isCorrect
         }]);
         if (isCorrect) setScore(prev => prev + 1);
         else setWrongCount(prev => prev + 1);
         setFeedback({ isCorrect });
         setIsFlipped(true);
         return;
      }

      if (currentIndex < words.length - 1) {
         goTo(currentIndex + 1);
      } else {
         const finishPath = isStudy ? '/Study/Finish' : '/Challenge/Finish';
         navigate(finishPath, {
            state: {
               score,
               total: words.length,
               wrong: wrongCount,
               details,
               chapter,
               words, // lets Study/Finish test exactly these words
               review: isReview
            }
         });
      }
   };

   const handleFlip = () => {
      setIsFlipped(!isFlipped);
   };

   if (words.length === 0) return <Layout containName='view'>Loading...</Layout>;

   const currentWord = words[currentIndex];
   const hintString = currentWord.word[0] + ' _ '.repeat(Math.max(0, currentWord.word.length - 1));

   return (
      <Layout header containName='view'>
         {!isStudy && !chapter && (
            <Popup2 popupName='challenge-stop' title={'잠깐!'} btn challenge>
               지금까지 푼 내용은 저장되지 않습니다.<br />나가시겠습니까?
            </Popup2>
         )}

         <div className="count-wrap">
            <div className="count">문항수 <div className="num-area"><span>{currentIndex + 1}</span>/<span>{words.length}</span></div></div>
            <div className="count wrong">오답 <div className="num-area"><span>{wrongCount}</span></div></div>
         </div>

         {/* Card Logic */}
         <div className={`card ${isFlipped ? 'active' : ''}`} onClick={handleFlip} style={{ cursor: 'pointer' }}>

            {/* FRONT */}
            <div className="front">
               {currentWord.level && <p className="grammar" style={{ color: '#888' }}>Level {currentWord.level}</p>}

               {/* STUDY: English. CHALLENGE: Korean. */}
               {isStudy ? (
                  <strong className="word" style={{ fontSize: '40px' }}>{currentWord.word}</strong>
               ) : (
                  <strong className="word" style={{ fontSize: '28px', wordBreak: 'keep-all' }}>{currentWord.meaning}</strong>
               )}

               {/* STUDY: Audio Button. CHALLENGE: Hint + Audio. */}
               {!isStudy ? (
                  <>
                     <div style={{ marginTop: '20px', minHeight: '30px' }} onClick={(e) => e.stopPropagation()}>
                        {!showHint ? (
                           <button type="button" onClick={() => setShowHint(true)} style={{ padding: '5px 10px', fontSize: '12px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                              💡 힌트 보기
                           </button>
                        ) : (
                           <div style={{ fontSize: '20px', letterSpacing: '3px', color: '#555', fontFamily: 'monospace' }}>
                              {hintString}
                           </div>
                        )}
                     </div>
                     {/* No audio on the challenge front: it would read the answer aloud */}
                  </>
               ) : (
                  <div style={{ marginTop: '10px' }} onClick={(e) => e.stopPropagation()}><AudioButton word={currentWord.word} /></div>
               )}
            </div>

            {/* BACK (Answer) */}
            <div className="back">
               {currentWord.level && <p className="grammar">Level {currentWord.level}</p>}

               {/* STUDY: Meaning. CHALLENGE: English. */}
               {isStudy ? (
                  <>
                     <strong className="word" style={{ fontSize: '28px', wordBreak: 'keep-all' }}>{currentWord.meaning}</strong>
                     <p className="meaning" style={{ fontSize: '15px', marginTop: '14px', textAlign: 'center', wordBreak: 'keep-all', overflowWrap: 'break-word', lineHeight: 1.5 }}>{currentWord.example_en}<br />{currentWord.example_kr}</p>
                  </>
               ) : (
                  <>
                     <strong className="word" style={{ fontSize: '40px' }}>{currentWord.word}</strong>
                     <p className="meaning" style={{ fontSize: '16px', marginTop: '10px', textAlign: 'center', wordBreak: 'keep-all' }}>{currentWord.meaning}</p>
                  </>
               )}

               <div style={{ marginTop: '10px' }} onClick={(e) => e.stopPropagation()}><AudioButton word={currentWord.word} /></div>
            </div>

         </div>

         <div className="write-answer">
            <p>{isStudy ? '단어를 입력해보세요 (선택)' : '영어 단어를 입력하세요'}</p>
            <input
               type="text"
               ref={inputRef}
               value={userInput}
               onChange={e => setUserInput(e.target.value)}
               // Enter on an empty challenge answer does nothing (avoids accidental "wrong"); the button still allows skipping
               onKeyDown={e => e.key === 'Enter' && !e.repeat && (isStudy || feedback || userInput.trim()) && handleNext()}
               readOnly={!!feedback}
               placeholder="English..."
               autoComplete="off"
               autoCapitalize="off"
            />
            {feedback && (
               <p style={{ marginTop: '10px', fontWeight: 'bold', color: feedback.isCorrect ? '#2e9e5b' : '#ff4d4d' }}>
                  {feedback.isCorrect ? '정답이에요!' : `아쉬워요. 정답은 ${currentWord.word}`}
               </p>
            )}
         </div>

         <div className="footer-btngroup">
            <button type="button" className="btn-next" onClick={handleNext} style={{ marginLeft: '0', width: '100%' }}>
               {!feedback && (!isStudy || userInput.trim())
                  ? '정답 확인'
                  : (currentIndex === words.length - 1 ? '결과 보기' : '다음 문제')}
            </button>
         </div>
      </Layout>
   );
};

export default ChallengeView;